import React, { useState, useEffect } from 'react';
import { getRecipe, addRecipe } from '../../utils/api'; // Ensure these functions are available
import { GoogleGenerativeAI } from "@google/generative-ai"; // Ensure this package is installed
import Loader from "../Loader";

const Recipes = () => {
    const [recipes, setRecipes] = useState([]);
    const [currentRecipeIndex, setCurrentRecipeIndex] = useState(0);
    const [error, setError] = useState(null);

    // Inject keyframes for fade-in animation
    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.innerHTML = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
        document.head.appendChild(styleEl);
        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);

    // Set up an interval to update the displayed recipe every 15 seconds.
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentRecipeIndex(prevIndex =>
                recipes.length > 0 ? (prevIndex + 1) % recipes.length : 0
            );
        }, 15000); // 15000ms = 15 seconds
        return () => clearInterval(interval);
    }, [recipes]);

    // Helper function to parse the Gemini response into an array of recipe objects.
    // The expected fields are:
    //   - שם מתכון        => name
    //   - קלוריות         => servings
    //   - זמן הכנה כולל   => preparationTime
    //   - מה הטעם המורגש  => flavorProfile
    //   - מרכיבים         => ingredients (a list, one per line)
    //   - הוראות הכנה     => instructions (a list of steps)
    //   - הנחיות לאלרגנים  => dietaryAllergies
    const parseRecipes = (text) => {
        // Remove the header line (if any)
        const cleanedText = text.replace(/^##.*\n/, '').trim();
        // Split into blocks based on recipe headers (**<number>. Recipe Name:**)
        const recipeBlocks = cleanedText.split(/(?=\*\*\d+\.\s)/);

        const parsedRecipes = recipeBlocks.map((block) => {
            // Extract the recipe name from a header like: **1. סלט קינואה עם עוף צלוי:**
            const nameMatch = block.match(/\*\*\d+\.\s*(.*?)\s*:\*\*/);
            const name = nameMatch ? nameMatch[1].trim() : 'No Name';

            // Extract calories (mapped to servings)
            const servingsMatch = block.match(/\*\s*\*\*קלוריות:\*\*\s*(.*?)\s*(?:\n|$)/);
            const servings = servingsMatch ? servingsMatch[1].trim() : '';

            // Extract preparation time
            const prepMatch = block.match(/\*\s*\*\*זמן הכנה כולל:\*\*\s*(.*?)\s*(?:\n|$)/);
            const preparationTime = prepMatch ? prepMatch[1].trim() : '';

            // Extract flavor profile
            const flavorMatch = block.match(/\*\s*\*\*הטעם המורגש:\*\*\s*(.*?)\s*(?:\n|$)/);
            const flavorProfile = flavorMatch ? flavorMatch[1].trim() : '';

            // --- Updated Ingredients Extraction ---
            const ingredientsMatch = block.match(/\*\s*\*\*מרכיבים:\*\*\s*([\s\S]*?)(?=\n\*\s*\*\*|$)/);
            let ingredients = [];
            if (ingredientsMatch && ingredientsMatch[1]) {
                ingredients = ingredientsMatch[1]
                    .split(',')
                    .map(ing => ing.replace(/^\s*\*\s*/, '').trim())
                    .filter(ing => ing.length > 0)
                    .map(ing => ({ name: ing, quantity: '' }));
            }

            // --- Updated Instructions Extraction ---
            const instructionsMatch = block.match(/\*\s*\*\*הוראות הכנה:\*\*\s*([\s\S]*?)(?=\n\*\s*\*\*|$)/);
            let instructions = [];
            if (instructionsMatch && instructionsMatch[1]) {
                instructions = instructionsMatch[1]
                    .split('.')///\n/)
                    .map(step => step.replace(/^\s*\d+\.\s*/, '').trim())
                    .filter(step => step.length > 0);
            }

            // Extract dietary allergies guidelines
            const allergiesMatch = block.match(/\*\s*\*\*הנחיות לאלרגנים:\*\*\s*([\s\S]*?)(?=\n\*\s*\*\*|$)/);
            const dietaryAllergies = allergiesMatch ? allergiesMatch[1].trim() : '';

            return {
                name,
                servings,
                preparationTime,
                flavorProfile,
                ingredients,
                instructions,
                dietaryAllergies
            };
        });

        // Filter out any blocks that might be empty
        return parsedRecipes.filter((r) => r.name && r.name !== 'No Name');
    };

    // Function to call the Google Gemini API once a month and save the response in the DB
    const callGemini = async () => {
        try {
            const currentMonth = new Date().getMonth();
            const lastCallMonth = localStorage.getItem('lastGeminiCallMonth');
            if (lastCallMonth && parseInt(lastCallMonth, 10) === currentMonth) {
                console.log('Gemini API already called this month.');
                return;
            }

            // The Hebrew prompt message
            const promptMessage = `תן לי 10 מתכונים רנדומלים שונים ,דלי קלוריות, שכל אחד כולל את : 
                שם מתכון:
                קלוריות:  
                זמן הכנה כולל:  
                מה הטעם המורגש: 
                מרכיבים:
                הוראות הכנה:
                הנחיות לאלרגנים:
                
                `;

            // Instantiate the Gemini API client and request content
            const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
            const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
            const result = await model.generateContent(promptMessage);
            const aiText = result.response.text();

            // Parse the Gemini API response into an array of recipe objects
            const newRecipes = parseRecipes(aiText);

            // Save each new recipe to the DB by calling your API
            for (const recipe of newRecipes) {
                if (
                    recipe.name && recipe.name.trim() !== '' &&
                    (
                        (recipe.instructions && recipe.instructions.length > 0) ||
                        (recipe.ingredients && recipe.ingredients.length > 0)
                    )
                ) {
                    try {
                        await addRecipe(recipe);
                    } catch (saveError) {
                        console.error('Error saving recipe to DB:');
                    }
                } else {
                    console.log("Skipping recipe due to lack of content:");
                }
            }

            // Optionally re-fetch all recipes from the backend after saving new ones
            const data = await getRecipe();
            setRecipes(data);

            // Mark that we've called Gemini this month
            localStorage.setItem('lastGeminiCallMonth', currentMonth.toString());
        } catch (err) {
            console.error('Error generating content:', err);
        }
    };

    // Fetch existing recipes from your API when the component mounts
    useEffect(() => {
        const fetchRecipes = async () => {
            try {
                const data = await getRecipe(); // Assumes this returns an array of recipes
                setRecipes(data);
            } catch (err) {
                console.error('Error fetching recipes:', err);
                setError('טעינת מתכונים נכשלה.');
            }
        };

        fetchRecipes();
        // Trigger the monthly Gemini API call
        callGemini();
    }, []);

    // Inline style objects with RTL settings and improved design
    const containerStyle = {
        minHeight: '600px',
        maxHeight: '100%',
        overflowY: 'auto',
        padding: '20px',
        direction: 'rtl',
        textAlign: 'right',
        fontFamily: 'GveretLevin'
    };

    const loadingErrorStyle = {
        textAlign: 'center',
        fontSize: '1.2em',
        padding: '20px',
        direction: 'rtl'
    };

    const cardBaseStyle = {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        animation: 'fadeIn 1s forwards',
        direction: 'rtl'
    };

    const titleStyle = {
        marginTop: 0,
        fontSize: '1.8em',
        color: '#333'
    };

    const sectionHeadingStyle = {
        marginTop: '8px',

        marginBottom: '8px',
        color: '#555'
    };
    const sectionstyle = {
        marginTop: '8px',
    };

    const listStyle = {
        margin: '10px 0',
        paddingRight: '20px'
    };

    return (
        <div style={containerStyle}>
            {error && <div style={loadingErrorStyle}>{error}</div>}
            {recipes.length === 0 && !error && (
                <div style={loadingErrorStyle}><Loader /></div>
            )}
            {recipes.length > 0 && (
                <div key={recipes[currentRecipeIndex]._id || currentRecipeIndex} style={cardBaseStyle}>
                    <h2 style={titleStyle}>{recipes[currentRecipeIndex].name}</h2>
                    <div style={sectionstyle}>
                        <p>
                            <strong>זמן הכנה כולל:</strong> {recipes[currentRecipeIndex].preparationTime}
                        </p>
                        <p>
                            <strong>קלוריות:</strong> {recipes[currentRecipeIndex].servings}
                        </p>
                    </div>
                    {recipes[currentRecipeIndex].flavorProfile && (
                        <p>
                            <strong>הטעם המורגש:</strong> {recipes[currentRecipeIndex].flavorProfile}
                        </p>
                    )}
                    <section>
                        <h3 style={sectionHeadingStyle}>מרכיבים</h3>
                        <div style={listStyle}>
                            {recipes[currentRecipeIndex].ingredients &&
                                recipes[currentRecipeIndex].ingredients.map((ing, idx) => (
                                    <p key={idx} style={{ margin: '5px 0' }}>
                                        {ing.quantity} {ing.name}
                                    </p>
                                ))}
                        </div>
                    </section>
                    <section>
                        <h3 style={sectionHeadingStyle}>הוראות הכנה</h3>
                        <ol style={listStyle}>
                            {recipes[currentRecipeIndex].instructions &&
                                recipes[currentRecipeIndex].instructions.map((step, idx) => (
                                    <li key={idx}>{step}</li>
                                ))}
                        </ol>
                    </section>
                    {recipes[currentRecipeIndex].dietaryAllergies && (
                        <section>
                            <h3 style={sectionHeadingStyle}>הנחיות לאלרגנים</h3>
                            <p>{recipes[currentRecipeIndex].dietaryAllergies}</p>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
};

export default Recipes;
